import PropTypes from 'prop-types';
import React from 'react';

import {
    ArticleStandardListingItem,
    ArticleFeaturedListingItem
} from '$partials/listings/items';

import {
    Footer,
    Header,
    Meta
} from '$partials/global';

import { AjaxListingContext } from '$partials/base/ajax-listing';
import { Button } from '$partials/ui';
import { CarouselProvider } from '$contexts/carousel';
import { useAjaxListing } from '$hooks/ajax-listing';
import { useLocale } from '$contexts/locale';
import Carousel, { CarouselSlide } from '$partials/ui/carousel';
import ModulePopular from '$partials/modules/individual/popular';

/* =========================================================================  */
/* Component(s) */
/* =========================================================================  */

/**
 * @param {PropTypes.InferProps<typeof propTypes>} props
 */
const HomePage = ({ data, domain }) => {
    // misc
    const page = data?.page;

    // hooks
    const {
        current: locale,
        formatURI,
        translate: t
    } = useLocale();

    const listing = useAjaxListing({
        items: data?.items?.items,
        page: data?.items?.page || 1,
        path: '/api/v1/listings/articles',
        totalItems: data?.items?.total,
        totalPages: data?.items?.totalPages,
        query: { locale: locale?.code  }
    });

    // misc
    if (!page) { return null; }

    const featuredArticles = page.featuredArticles?.items;
    const featuredSections = page.featuredSections?.items;

    function shuffleArray(array) {
        if (!array) {
            return;
        }
        for (let i = array.length - 1; i > 0; i--) {
            const j = Math.floor(Math.random() * (i + 1));
            [array[i], array[j]] = [array[j], array[i]];
        }
        return array;
    }

    // render
    return (
        <>
            <Meta
                page={page}
                type='Article' />

            <main
                className='bg-grey min-h-screen'
                id='main-content'>
                <div className='layout-container'>
                    <Header
                        menu={data?.navigation}
                        search={data?.search}/>

                    <article className='pt-60'>
                        <h1 className='hidden'>{page.seoMetadata?.title || 'Strava Stories'}</h1>
                        {/* Hero Article: Article */}
                        {page.heroArticle && page.heroArticle.__typename === 'BvaMagazineArticlePage' && (
                            <section className='px-10 sm:px-20 mb-60 md:mb-70'>
                                <a href={`/articles/${page.heroArticle.slug}`}>
                                    <div className='md:pr-100'>
                                        <h2 className='type-style-2 inline mr-10'>{page.heroArticle?.title}</h2>
                                    </div>

                                    <div className='mt-30 flex flex-wrap items-center'>
                                        {page.heroArticle?.activities?.items.length > 0 && (
                                            <>
                                                <div className='mr-5'>
                                                    <img
                                                        alt={page.heroArticle.activities.items[0].icon.alt}
                                                        className='pointer-events-none'
                                                        height='23'
                                                        loading='lazy'
                                                        role='presentation'
                                                        src={page.heroArticle.activities.items[0].icon.src}
                                                        width='23' />
                                                </div>

                                                <p className='inline-block type-style-7 text-orange '>
                                                    {page.heroArticle?.activities?.items?.[0]?.title}
                                                </p>
                                            </>
                                        )}

                                        {page.heroArticle?.author && (
                                            <p className='w-full sm:w-3/12 inline-block type-style-7 text-dark-grey sm:ml-40 mt-10 sm:mt-0 '>
                                                {`${t('by')} ${page.heroArticle.author.name}`}
                                            </p>
                                        )}
                                    </div>

                                    {page.heroArticle?.listingFields?.listingImage && (
                                        <figure className='mt-30'>
                                            <div className='left-1/2 -translate-x-1/2 relative w-screen px-10 '>
                                                <div
                                                    className='min-h-[200px] sm:min-h-[422px] md:min-h-[475px] w-full bg-contain bg-repeat-x'
                                                    style={{backgroundImage: `url(${page.heroArticle?.listingFields?.listingImage?.src})`}}
                                                    title={page.heroArticle?.listingFields?.listingImage.title} />
                                            </div>
                                        </figure>
                                    )}
                                </a>
                            </section>
                        )}

                        {/* Hero Article: Awards Landing/Nominees Page */}
                        {page.heroArticle && (page.heroArticle.__typename === 'BvaMagazineAwardsLandingPage' || page.heroArticle.__typename === 'BvaMagazineAwardsNomineesPage') && (
                            <section className='px-10 sm:px-20 mb-60 md:mb-70'>
                                <a href={page.heroArticle.__typename === 'BvaMagazineAwardsLandingPage' ? formatURI(`/year-in-sport/${page.heroArticle.slug}/winners`) : formatURI(`/year-in-sport/${page.heroArticle.slug}/nominees`)}>

                                    {page.heroArticle?.listingFields?.listingImage && (
                                        <figure>
                                            <div className='relative w-full'>
                                                <div
                                                    className='min-h-[200px] sm:min-h-[422px] md:min-h-[475px] w-full bg-cover'
                                                    style={{ backgroundImage: `url(${page.heroArticle.listingFields.listingImage.src})` }}
                                                    title={page.heroArticle.listingFields.listingImage.title} />
                                            </div>
                                        </figure>
                                    )}
                                </a>
                            </section>
                        )}

                        {/* Featured articles */}
                        {featuredArticles?.length > 0 && (
                            <section>
                                <div className='px-10 sm:px-20'>
                                    <h2 className='type-style-2 inline'>
                                        {page.featuredArticlesTitle}
                                    </h2>
                                </div>

                                {/* Desktop / Tablet */}
                                <div className='w-full hidden sm:flex flex-wrap mt-40'>
                                    <div className='w-full md:w-1/2'>
                                        <div className='sticky top-0'>
                                            <article className='px-10 sm:px-20'>
                                                <ArticleStandardListingItem {...featuredArticles[0]} />
                                            </article>
                                        </div>
                                    </div>

                                    {featuredArticles.slice(1, featuredArticles.length)?.length > 0 && (
                                        <div className='w-full md:w-1/2 flex flex-wrap'>
                                            {featuredArticles.slice(1, featuredArticles.length).map((item, i) => {
                                                if (!item) {
                                                    return null;
                                                }

                                                return (
                                                    <article
                                                        aria-label={item.title}
                                                        aria-posinset={i + 1}
                                                        className={`flex ${(i + 1) % 3 === 0 ? 'w-full' : 'w-1/2'} mt-50 ${i > 1 ? 'md:mt-70' : 'md:mt-0'} px-10 sm:px-20`}
                                                        key={i}
                                                        tabIndex={-1}>
                                                        <ArticleStandardListingItem {...item} />
                                                    </article>
                                                );
                                            })}
                                        </div>
                                    )}
                                </div>

                                {/* Mobile */}
                                <div className='sm:hidden'>
                                    <CarouselProvider>
                                        <Carousel className='flex swiper-wrapper h-auto w-full overflow-hidden'>
                                            {featuredArticles.map((item, i) => {
                                                if (!item) {
                                                    return null;
                                                }

                                                return (
                                                    <CarouselSlide
                                                        className='h-auto flex flex-wrap w-9/12 px-10'
                                                        index={i}
                                                        key={`featured-article-${i}`}>
                                                        <article
                                                            aria-label={item.title}
                                                            aria-posinset={i + 1}
                                                            className='w-full'
                                                            tabIndex={-1}>
                                                            <ArticleStandardListingItem {...item} />
                                                        </article>
                                                    </CarouselSlide>
                                                );})}
                                        </Carousel>
                                    </CarouselProvider>
                                </div>
                            </section>
                        )}

                        {/* Featured Sections */}
                        {featuredSections?.length > 0 && featuredSections.map((section, idx) => {
                            if (!section?.__typename) {
                                return null;
                            }
                            let shuffledSection;
                            if (section?.title !== 'What\'s New') {
                                shuffledSection = shuffleArray(section?.items)?.slice(0, 3);
                            } else {
                                shuffledSection = section?.items?.slice(0, 3);
                            }

                            return (
                                <section
                                    className='mt-60 md:mt-70'
                                    key={`featured-section-${idx}`}>
                                    {(section.__typename === 'BvaMagazineSportPage' || section.__typename === 'BvaMagazineTopicPage')
                                    && section.items?.length > 0 ? (
                                            <>
                                                <div className='px-10 sm:px-20 pb-40 flex items-center'>
                                                    <h2 className='type-style-2 inline'>{section.title}</h2>
                                                    <Button
                                                        as='a'
                                                        className='flex items-center bg-orange text-white rounded type-style-7 px-20 py-15 ml-20 '
                                                        href={`${section.__typename === 'BvaMagazineSportPage' ? '/activities/' : section.__typename === 'BvaMagazineTopicPage' ? '/topics/' : ''}${section.slug}`}
                                                        type='button'>
                                                        {t('All Stories')}
                                                    </Button>
                                                </div>
                                                <div className='hidden md:flex'>
                                                    {shuffledSection.map((item, i) => (
                                                        item ? (
                                                            <article
                                                                aria-label={item.title}
                                                                aria-posinset={i + 1}
                                                                className={'flex w-full sm:w-4/12 px-10 sm:px-20'}
                                                                key={i}
                                                                tabIndex={-1}>
                                                                <ArticleStandardListingItem {...item} />
                                                            </article>
                                                        ) : null
                                                    ))}
                                                </div>
                                                <div className='overflow-hidden w-screen max-w-screen left-1/2 md:hidden relative -translate-x-1/2'>
                                                    <CarouselProvider>
                                                        <Carousel className='flex swiper-wrapper h-auto w-full image-carousel feature-section'>
                                                            {shuffledSection.map((item, i) => {
                                                                if (!item) {
                                                                    return null;
                                                                }

                                                                return (
                                                                    <CarouselSlide
                                                                        className='h-auto flex flex-wrap image-carousel-slide px-10 sm:px-20'
                                                                        index={idx}
                                                                        key={`${section.title}-${idx}-item-${i}`}>
                                                                        <article
                                                                            aria-label={item.title}
                                                                            aria-posinset={i + 1}
                                                                            className='w-full'
                                                                            tabIndex={-1}>
                                                                            <ArticleStandardListingItem {...item} />
                                                                        </article>
                                                                    </CarouselSlide>
                                                                );})}
                                                        </Carousel>
                                                    </CarouselProvider>
                                                </div>
                                            </>
                                        ) :  section.__typename === 'BvaMagazineModulePopular' ? (
                                            <ModulePopular
                                                articles={data?.popular}
                                                section={section}/>
                                        ) :(
                                            <ArticleFeaturedListingItem
                                                article={section}
                                                backgroundColor={section.backgroundColor}/>
                                        )}

                                </section>
                            );})}

                        {/* Listing articles */}
                        <AjaxListingContext.Provider value={listing}>
                            <section
                                aria-busy={listing.loading}
                                aria-label='Articles'
                                className='flex flex-wrap mt-60 md:mt-70'
                                role='feed'>
                                <div className='w-full px-10 sm:px-20 mb-30 flex flex-wrap items-center'>
                                    <h2 className='type-style-2 inline mr-10'>
                                        {t('Latest Stories')}
                                    </h2>

                                    <div className='w-full md:w-auto flex md:px-20 mt-20 md:mt-0'>
                                        <Button
                                            as='a'
                                            className='flex self-start items-center bg-orange text-white  rounded type-style-7 px-20 py-15'
                                            href='articles'
                                            type='button'>
                                            {t('View All')}
                                        </Button>
                                    </div>
                                </div>

                                {listing?.items?.map((item, i) => {
                                    if (!item) {
                                        return null;
                                    }
                                    return (
                                        <article
                                            aria-label={item.title}
                                            aria-posinset={i + 1}
                                            aria-setsize={listing.totalItems}
                                            className={'flex flex-col w-full sm:w-6/12 md:w-4/12 pb-50 md:pb-70 px-10 sm:px-20'}
                                            key={i}
                                            tabIndex={-1}>
                                            <ArticleStandardListingItem {...item} />
                                        </article>
                                    );
                                })}
                            </section>

                            {listing.totalPages > 1 && (
                                <div className='w-full flex px-10 sm:px-20 mt-30'>
                                    <Button
                                        as='a'
                                        className='flex self-start items-center bg-orange text-white  rounded type-style-7 px-20 py-15'
                                        href='articles'
                                        type='button'>
                                        {t('All Stories')}
                                    </Button>
                                </div>
                            )}
                        </AjaxListingContext.Provider>
                    </article>

                    <div className='border-b mt-60 md:mt-80 mx-10 sm:mx-20'/>

                    <Footer
                        domain={domain}
                        menu={data?.footer}/>
                </div>
            </main>
        </>
    );
};

/* =========================================================================  */
/* Typing and exports */
/* =========================================================================  */

const propTypes = {
    data: PropTypes.shape({
        items: PropTypes.object,
        page: PropTypes.shape({
            title: PropTypes.string,
            modules: PropTypes.shape({
                items: PropTypes.array
            })
        }),
        popular: PropTypes.shape({
            items: PropTypes.array,
        }),
        footer: PropTypes.shape({
            items: PropTypes.array
        }),
        navigation: PropTypes.object,
        search: PropTypes.object,
    }),
    domain: PropTypes.string,
};

HomePage.propTypes = propTypes;

export default HomePage;
